import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mic, MicOff, Camera, Save, X, ArrowRight, Sparkles, Loader2, Check } from "lucide-react";
import IOSInstallPrompt from "@/components/IOSInstallPrompt";
import { compressImage } from "@/lib/imageCompress";

/**
 * Mobile-first, one-thumb, offline-friendly capture page for the sourcing workflow.
 *
 * Designed for a seller standing in a thrift shop with an iPhone in one hand and a
 * piece in the other. The flow is:
 *   1. Tap mic → speak the piece → auto-fills brand/type/color/size/cost
 *   2. Tap camera → snap 3-6 photos → they attach to this draft
 *   3. Tap "save & next" → record is created at "Studio Sourcing" stage, photos
 *      tagged role="sourcing" (internal), then the form resets for the next piece.
 *
 * Everything else about the piece can be filled in later on desktop.
 */
export default function MobileCapture() {
  const navigate = useNavigate();

  // Form state
  const emptyForm = {
    title: "", brand: "", type: "", color: "", fabric: "", size: "", style: "",
    condition: "", purchase_cost: "", source_vendor: "", purchase_date: "",
    description: "", notes: "",
  };
  const [form, setForm] = useState(emptyForm);
  const [photos, setPhotos] = useState([]); // storage paths
  const [transcript, setTranscript] = useState("");

  // Voice
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Photos
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });

  // Save
  const [saving, setSaving] = useState(false);
  const [role, setRole] = useState("sourcing"); // sourcing (default field capture) | studio | condition

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // ---------- Voice recording ----------
  const startRecording = async () => {
    if (recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Pick the widest-compat mime — WebM opus on Android/desktop, mp4 on iOS Safari.
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm"
                 : MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4"
                 : "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : {});
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: mime || "audio/webm" });
        await transcribeAudio(blob, mime || "audio/webm");
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      toast.error("microphone permission denied — check iphone settings.");
    }
  };

  const stopRecording = () => {
    if (!recording) return;
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  const transcribeAudio = async (blob, mime) => {
    setTranscribing(true);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = reject;
        reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
        reader.readAsDataURL(blob);
      });
      const { data } = await api.post("/inventory/voice-capture", { audio_base64: b64, mime });
      setTranscript(data.transcript || "");
      if (data.warning) toast.warning(data.warning);
      // Merge extracted fields into the form — never overwrite what the user has already typed.
      const fields = data.fields || {};
      setForm((prev) => {
        const merged = { ...prev };
        for (const [k, v] of Object.entries(fields)) {
          if (v == null || v === "") continue;
          if (!merged[k] || merged[k] === "") merged[k] = String(v);
        }
        return merged;
      });
      if (Object.keys(fields).length) toast.success("caught " + Object.keys(fields).length + " field(s).");
    } catch (e) {
      toast.error(e.response?.data?.detail || "transcription failed.");
    } finally {
      setTranscribing(false);
    }
  };

  // ---------- Photos ----------
  const capturePhotos = async (fileList) => {
    if (!fileList?.length) return;
    setUploading(true);
    setUploadProgress({ done: 0, total: fileList.length });
    try {
      let done = 0;

      const uploadOne = async (raw) => {
        // iPhone photos come in at 3-4 MB each; compress client-side so cellular
        // uploads land quickly and the save button unblocks sooner. Shared with
        // the desktop uploader — see /app/frontend/src/lib/imageCompress.js.
        const file = await compressImage(raw);
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        done += 1;
        setUploadProgress({ done, total: fileList.length });
        return data.path;
      };
      // Use allSettled so one failed photo doesn't wipe the successful ones.
      // Cap concurrency to 3 — on cellular, 6+ parallel uploads throttle each
      // other; a small pool completes wall-clock faster.
      const CONCURRENCY = 3;
      const queue = [...fileList];
      const settled = new Array(fileList.length);
      const workers = Array.from({ length: Math.min(CONCURRENCY, fileList.length) }, async () => {
        while (queue.length) {
          const idx = fileList.length - queue.length;
          const raw = queue.shift();
          try {
            settled[idx] = { status: "fulfilled", value: await uploadOne(raw) };
          } catch (err) {
            settled[idx] = { status: "rejected", reason: err };
          }
        }
      });
      await Promise.all(workers);
      const paths = settled.filter((r) => r && r.status === "fulfilled").map((r) => r.value);
      const failed = fileList.length - paths.length;
      setPhotos((prev) => [...prev, ...paths]);
      if (failed > 0) {
        toast.warning(`${paths.length} attached · ${failed} failed — please retake.`);
      } else {
        toast.success(`${paths.length} photo${paths.length === 1 ? "" : "s"} attached.`);
      }
    } catch (e) {
      toast.error("upload failed — try again.");
    } finally {
      setUploading(false);
      setUploadProgress({ done: 0, total: 0 });
    }
  };

  const removePhoto = (path) => setPhotos((prev) => prev.filter((p) => p !== path));

  // ---------- Save ----------
  const saveAndNext = async () => {
    // Block save while photos are still uploading — this was the #1 field bug:
    // saved records ended up with empty photos arrays because the user tapped
    // "save & next" before uploads finished.
    if (uploading) {
      toast.error("photos still uploading — wait a moment.");
      return;
    }
    if (transcribing) {
      toast.error("voice still processing — wait a moment.");
      return;
    }
    if (!photos.length && !form.title && !form.brand) {
      toast.error("add at least a photo or a brand/title.");
      return;
    }
    if (!photos.length) {
      if (!window.confirm("no photos attached — save this piece anyway?")) return;
    }
    setSaving(true);
    try {
      // Build the payload. Numeric field parsing is defensive because voice extraction
      // may leave `purchase_cost` as a string.
      const purchaseCost = form.purchase_cost === "" ? 0 : Number(form.purchase_cost) || 0;
      // photo_roles: mark every photo with the chosen role (default sourcing).
      const photo_roles = {};
      photos.forEach((p) => { photo_roles[p] = role; });
      const payload = {
        ...form,
        purchase_cost: purchaseCost,
        photos,
        photo_roles,
        stage: "Studio Sourcing",
        source_of_capture: "mobile_pwa",
        // Sourcing photos should NOT auto-publish — clear the channel toggles so
        // the piece doesn't leak to the storefront before studio photos are taken.
        photo_channels: (() => {
          const m = {};
          if (role === "sourcing" || role === "condition") {
            photos.forEach((p) => { m[p] = []; });
          }
          return m;
        })(),
      };
      const { data } = await api.post("/inventory", payload);
      toast.success(`piece saved · ${data.sku || data.id.slice(0, 8)}. ready for the next one.`);
      // Reset for the next piece.
      setForm(emptyForm);
      setPhotos([]);
      setTranscript("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "save failed.");
    } finally {
      setSaving(false);
    }
  };

  // ---------- Auto-install PWA prompt ----------
  useEffect(() => {
    // No behaviour — just document.title for the home-screen icon label.
    document.title = "Maison Pilet · capture";
  }, []);

  const fld = (k, label, placeholder, opts = {}) => (
    <label className="flex flex-col" data-testid={`mcap-${k}`}>
      <span className="text-[10px] uppercase tracking-[0.2em] text-taupe mb-1">{label}</span>
      <input
        type={opts.type || "text"}
        inputMode={opts.inputMode}
        step={opts.step}
        value={form[k]}
        onChange={(e) => upd(k, e.target.value)}
        placeholder={placeholder}
        className="border border-taupe/40 bg-white px-3 py-2.5 text-sm text-charcoal focus:outline-none focus:border-brass"
      />
    </label>
  );

  return (
    <div className="min-h-screen bg-linen pb-32" data-testid="mobile-capture-page">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-ivory/95 backdrop-blur border-b border-taupe/30 px-4 py-3 flex items-center justify-between">
        <button onClick={() => navigate("/admin")} data-testid="mcap-close" className="text-taupe -m-1 p-1">
          <X className="w-5 h-5" />
        </button>
        <h1 className="font-serif text-xl lowercase text-charcoal">quick capture</h1>
        <button
          onClick={saveAndNext}
          disabled={saving || uploading || transcribing}
          data-testid="mcap-save-btn"
          className="text-xs uppercase tracking-widest text-brass disabled:opacity-40 flex items-center gap-1"
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
          {saving ? "saving" : uploading ? "wait…" : "save"}
        </button>
      </header>

      {/* iOS install-to-home-screen prompt — appears once on iOS Safari when the
          user hasn't installed the PWA yet. Dismissable and remembered per device. */}
      <IOSInstallPrompt />

      {/* Voice */}
      <section className="px-4 pt-5 pb-3">
        <p className="text-[10px] uppercase tracking-[0.2em] text-taupe mb-2">voice</p>
        <div className="flex items-center gap-3">
          <button
            onClick={recording ? stopRecording : startRecording}
            disabled={transcribing}
            data-testid="mcap-mic-btn"
            className={`flex-1 px-4 py-4 flex items-center justify-center gap-2 text-xs uppercase tracking-widest transition-colors ${
              recording
                ? "bg-red-700 text-ivory animate-pulse"
                : transcribing
                ? "bg-taupe text-ivory"
                : "bg-charcoal text-ivory hover:bg-brass"
            }`}
          >
            {transcribing ? <Loader2 className="w-4 h-4 animate-spin" /> : recording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            {transcribing ? "transcribing…" : recording ? "tap to stop" : "tap to speak"}
          </button>
        </div>
        {transcript && (
          <p className="mt-3 text-xs italic text-espresso bg-ivory border border-taupe/30 px-3 py-2" data-testid="mcap-transcript">
            &ldquo;{transcript}&rdquo;
          </p>
        )}
        <p className="mt-2 text-[10px] text-taupe">
          e.g. &ldquo;cream cashmere cardigan size medium, everlane, twenty-eight dollars from the fillmore salvation army&rdquo;
        </p>
      </section>

      {/* Form (compact grid, thumb-friendly) */}
      <section className="px-4 pb-5">
        <p className="text-[10px] uppercase tracking-[0.2em] text-taupe mb-2">the piece</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">{fld("title", "title / hook", "cream cashmere cardigan")}</div>
          {fld("brand", "brand", "everlane")}
          {fld("type", "type", "cardigan")}
          {fld("color", "color", "cream")}
          {fld("fabric", "fabric", "cashmere")}
          {fld("size", "size", "M")}
          {fld("style", "style", "casual")}
          {fld("condition", "condition", "excellent")}
          {fld("purchase_cost", "cost $", "28", { type: "number", inputMode: "decimal", step: "0.01" })}
          <div className="col-span-2">{fld("source_vendor", "source vendor", "salvation army · fillmore")}</div>
          {fld("purchase_date", "purchase date", "", { type: "date" })}
          <div className="col-span-2">
            <span className="text-[10px] uppercase tracking-[0.2em] text-taupe mb-1 block">notes</span>
            <textarea
              value={form.notes}
              onChange={(e) => upd("notes", e.target.value)}
              rows={2}
              placeholder="pinhole on left cuff · reweave"
              className="w-full border border-taupe/40 bg-white px-3 py-2 text-sm text-charcoal"
              data-testid="mcap-notes"
            />
          </div>
        </div>
      </section>

      {/* Role picker */}
      <section className="px-4 pb-5">
        <p className="text-[10px] uppercase tracking-[0.2em] text-taupe mb-2">photo role</p>
        <div className="grid grid-cols-3 gap-2" data-testid="mcap-role-picker">
          {[
            { key: "sourcing", label: "sourcing", hint: "as-acquired · internal" },
            { key: "condition", label: "condition", hint: "defects · internal" },
            { key: "studio", label: "studio", hint: "publishable" },
          ].map((r) => (
            <button
              key={r.key}
              onClick={() => setRole(r.key)}
              data-testid={`mcap-role-${r.key}`}
              className={`px-2 py-2 border text-left transition-colors ${
                role === r.key ? "border-brass bg-brass/10 text-charcoal" : "border-taupe/40 text-espresso"
              }`}
            >
              <p className="text-xs lowercase font-medium">{r.label}</p>
              <p className="text-[9px] text-taupe mt-0.5">{r.hint}</p>
            </button>
          ))}
        </div>
      </section>

      {/* Photos */}
      <section className="px-4 pb-8">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] uppercase tracking-[0.2em] text-taupe">photos ({photos.length})</p>
          {photos.length > 0 && <button onClick={() => setPhotos([])} className="text-[10px] uppercase tracking-widest text-taupe">clear all</button>}
        </div>
        <div className="grid grid-cols-3 gap-2 mb-3">
          {photos.map((p) => (
            <div key={p} className="relative aspect-square bg-white border border-taupe/30" data-testid={`mcap-photo-thumb`}>
              <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`} alt="" className="w-full h-full object-cover" />
              <button
                onClick={() => removePhoto(p)}
                className="absolute top-1 right-1 bg-charcoal/80 text-ivory rounded-full p-1"
                aria-label="remove"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
        <label className="block w-full">
          <div className="w-full py-4 flex items-center justify-center gap-2 border border-charcoal text-charcoal text-xs uppercase tracking-widest bg-white active:bg-linen">
            <Camera className="w-4 h-4" />
            {uploading
              ? uploadProgress.total > 1
                ? `uploading · ${uploadProgress.done}/${uploadProgress.total}`
                : "uploading…"
              : "take photos"}
          </div>
          <input
            type="file"
            accept="image/*,.heic,.heif"
            capture="environment"
            multiple
            disabled={uploading}
            className="hidden"
            data-testid="mcap-photo-input"
            onChange={(e) => {
              capturePhotos(Array.from(e.target.files || []));
              // Reset so re-picking the same file (e.g. after a failed upload)
              // still fires onChange the next time.
              e.target.value = "";
            }}
          />
        </label>
      </section>

      {/* Sticky footer save + next.
          On iOS Safari the tab-bar hides on scroll and reappears on tap of the
          bottom edge; padding-bottom uses env(safe-area-inset-bottom) so we don't
          sit under Safari's chrome or block those bottom-edge taps. */}
      <div
        className="fixed bottom-0 left-0 right-0 bg-ivory border-t border-taupe/40 px-4 py-3 flex gap-3 z-30"
        style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
      >
        <button
          onClick={saveAndNext}
          disabled={saving || uploading || transcribing}
          data-testid="mcap-save-next-btn"
          className="flex-1 bg-brass hover:bg-espresso disabled:opacity-40 text-ivory px-5 py-4 text-xs tracking-[0.25em] uppercase flex items-center justify-center gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          {saving ? "saving…" : uploading ? `uploading photos · ${uploadProgress.done}/${uploadProgress.total}` : `save & next · ${photos.length} photo${photos.length === 1 ? "" : "s"}`}
          {!saving && !uploading && <ArrowRight className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
